import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { it } from "date-fns/locale";

import { api, errMsg } from "../lib/api";
import { usePermissions } from "../stores/permissions";
import { TopBar } from "../components/ui/TopBar";
import { Icon } from "../components/ui/Icon";
import { Button } from "../components/ui/Button";
import { Banner } from "../components/ui/Banner";
import { Modal } from "../components/ui/Modal";
import { SectionTitle } from "../components/ui/SectionTitle";
import { ManualProductModal } from "../components/products/ManualProductModal";

type Product = {
  sku: string;
  name: string;
  category: string | null;
  category_id: number | null;
  brand: string | null;
  image_url: string | null;
  origin: string;
  sync_to_shopify: boolean;
  quantity: number;
  low_stock: boolean;
  low_stock_threshold: number;
};

type ProductsResponse = { items: Product[]; total: number };

type Category = { id: number; name: string; product_count: number };

type Counts = Record<string, number | "">;

const PSEUDO_TUTTI = "Tutti";
const PSEUDO_DELTA = "Solo delta";
const PSEUDO_UNCAT = "Senza categoria";

export function Inventario() {
  const nav = useNavigate();
  const { hasPermission } = usePermissions();
  const [manualOpen, setManualOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [chip, setChip] = useState<string>(PSEUDO_TUTTI);
  const [counts, setCounts] = useState<Counts>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Product | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const canDelete = hasPermission("manual_product_delete");

  const { data: cats } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/api/categories")).data,
  });

  const selectedCat = cats?.find((c) => c.name === chip);
  const categoryId = selectedCat?.id;
  const uncategorized = chip === PSEUDO_UNCAT;

  const { data, refetch, isFetching } = useQuery<ProductsResponse>({
    queryKey: ["inventory", search, categoryId ?? null, uncategorized],
    queryFn: async () =>
      (
        await api.get("/api/products", {
          params: {
            search: search || undefined,
            category_id: categoryId,
            uncategorized: uncategorized || undefined,
            page_size: 200,
          },
        })
      ).data,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const catName = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of cats ?? []) m.set(c.id, c.name);
    return m;
  }, [cats]);

  const filtered = useMemo(() => {
    if (chip === PSEUDO_DELTA) {
      return items.filter((p) => {
        const c = counts[p.sku];
        return typeof c === "number" && c !== p.quantity;
      });
    }
    return items;
  }, [items, counts, chip]);

  const stats = useMemo(() => {
    let match = 0;
    let delta = 0;
    let pending = 0;
    for (const p of items) {
      const c = counts[p.sku];
      if (c === undefined || c === "") {
        pending++;
      } else if (c === p.quantity) {
        match++;
      } else {
        delta++;
      }
    }
    const counted = items.length - pending;
    const progressPct = items.length === 0 ? 0 : Math.round((counted / items.length) * 100);
    return { match, delta, pending, counted, progressPct };
  }, [items, counts]);

  const deltas = useMemo(() => {
    const out: { sku: string; delta: number; name: string }[] = [];
    for (const p of items) {
      const c = counts[p.sku];
      if (typeof c !== "number") continue;
      const d = c - p.quantity;
      if (d === 0) continue;
      out.push({ sku: p.sku, delta: d, name: p.name });
    }
    return out;
  }, [items, counts]);

  async function apply() {
    if (deltas.length === 0 || busy) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      for (const d of deltas) {
        await api.post(
          "/api/stock-event",
          {
            event_type: "inventory_adjust",
            sku: d.sku,
            quantity_delta: d.delta,
            reason: "inventario_fisico",
            notes: `Δ ${d.delta > 0 ? "+" : ""}${d.delta} · conta periodica`,
          },
          { headers: { "X-Idempotency-Key": crypto.randomUUID() } },
        );
      }
      setMsg(`${deltas.length} correzion${deltas.length === 1 ? "e applicata" : "i applicate"}.`);
      setCounts({});
      await refetch();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!toDelete || deleteBusy) return;
    setDeleteBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.delete(`/api/products/${encodeURIComponent(toDelete.sku)}`);
      setMsg(`Prodotto ${toDelete.sku} eliminato.`);
      setToDelete(null);
      await refetch();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  const sessionLabel = format(new Date(), "MMMM yyyy", { locale: it });
  const sessionCap = sessionLabel.charAt(0).toUpperCase() + sessionLabel.slice(1);

  return (
    <>
      <TopBar back onBack={() => nav("/")} title="Inventario Fisico" sub={`Conta periodica · ${sessionCap}`} />

      <div style={{ padding: "14px 18px 0" }}>
        <div className="tcg-card" style={{ padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "var(--tcg-muted)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Sessione conta
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, marginTop: 2, letterSpacing: "-0.01em" }}>
                {sessionCap} — Sede
              </div>
            </div>
            <div className="tcg-num" style={{ fontSize: 22, fontWeight: 900, color: "var(--tcg-navy)" }}>
              {stats.progressPct}%
            </div>
          </div>
          <div
            style={{
              height: 6,
              background: "var(--tcg-bg)",
              borderRadius: 3,
              overflow: "hidden",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: `${stats.progressPct}%`,
                height: "100%",
                background: "linear-gradient(90deg, var(--tcg-navy) 0%, var(--tcg-yellow) 100%)",
                transition: "width .25s",
              }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <SessionStat label="Quadrati" value={stats.match} tone="success" />
            <SessionStat label="Δ Delta" value={stats.delta} tone="warn" />
            <SessionStat label="Pendenti" value={stats.pending} tone="muted" />
          </div>
        </div>
      </div>

      <div style={{ padding: "12px 18px 0" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "var(--tcg-card)",
            border: "1.5px solid var(--tcg-border)",
            borderRadius: 12,
            padding: "10px 13px",
          }}
        >
          <Icon name="search" size={14} />
          <input
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 13,
              fontWeight: 600,
              background: "transparent",
            }}
            placeholder="Cerca per SKU o nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isFetching && <span style={{ fontSize: 10, color: "var(--tcg-muted)" }}>···</span>}
        </div>
        <div
          style={{
            display: "flex",
            gap: 6,
            marginTop: 10,
            overflowX: "auto",
            scrollbarWidth: "none",
            paddingBottom: 2,
          }}
        >
          {[PSEUDO_TUTTI, PSEUDO_DELTA, PSEUDO_UNCAT].map((c) => (
            <button
              key={c}
              type="button"
              className={`tcg-chip ${chip === c ? "on" : ""}`}
              onClick={() => setChip(c)}
            >
              {c === PSEUDO_DELTA && <span>⚠</span>}
              {c}
            </button>
          ))}
          {(cats ?? [])
            .filter((c) => c.product_count > 0)
            .map((c) => (
              <button
                key={c.id}
                type="button"
                className={`tcg-chip ${chip === c.name ? "on" : ""}`}
                onClick={() => setChip(c.name)}
              >
                {c.name} · {c.product_count}
              </button>
            ))}
        </div>
      </div>

      {hasPermission("manual_product_create") && (
        <div style={{ padding: "10px 18px 0" }}>
          <button
            type="button"
            className="tcg-btn tcg-btn-secondary tcg-btn-block"
            onClick={() => setManualOpen(true)}
          >
            <Icon name="tag" size={16} /> Nuovo prodotto manuale
          </button>
        </div>
      )}

      <SectionTitle
        right={
          <span style={{ paddingRight: 18, fontSize: 10, color: "var(--tcg-muted)", fontWeight: 700 }}>
            <span className="tcg-num">{filtered.length}</span> / <span className="tcg-num">{total.toLocaleString("it-IT")}</span> SKU
          </span>
        }
      >
        Conta SKU
      </SectionTitle>

      <div style={{ padding: "0 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.length === 0 && (
          <div className="tcg-card" style={{ padding: 16, textAlign: "center", color: "var(--tcg-muted)", fontSize: 12 }}>
            {chip === PSEUDO_DELTA
              ? "Nessun delta da rivedere in questa sessione."
              : "Nessun prodotto trovato."}
          </div>
        )}
        {filtered.map((p) => {
          const cat = inferCategory(p);
          const counted = counts[p.sku];
          const d = deltaInfo(counted, p.quantity);
          return (
            <div
              key={p.sku}
              className="tcg-card"
              style={{ padding: 11, display: "flex", gap: 11, alignItems: "center" }}
            >
              <ProductThumb imageUrl={p.image_url} fallback={cat} warnNoShopify={!p.sync_to_shopify} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.name}
                </div>
                <div
                  className="tcg-mono"
                  style={{ fontSize: 9.5, color: "var(--tcg-muted)", fontWeight: 600, marginTop: 1 }}
                >
                  {p.sku} · {(p.category_id != null && catName.get(p.category_id)) || p.category || "—"}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: 8.5,
                      color: "var(--tcg-muted)",
                      fontWeight: 800,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    SIST
                  </div>
                  <div className="tcg-num" style={{ fontSize: 13, fontWeight: 800, color: "var(--tcg-ink-soft)" }}>
                    {p.quantity}
                  </div>
                </div>
                <div style={{ color: "var(--tcg-muted-soft)", fontSize: 14 }}>→</div>
                <input
                  type="number"
                  inputMode="numeric"
                  className="tcg-input tcg-num"
                  value={counted === undefined ? "" : counted}
                  placeholder="—"
                  onChange={(e) => {
                    const v = e.target.value;
                    setCounts((c) => {
                      if (v === "") {
                        const { [p.sku]: _omit, ...rest } = c;
                        return rest;
                      }
                      return { ...c, [p.sku]: Math.max(0, parseInt(v, 10) || 0) };
                    });
                  }}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{
                    width: 60,
                    padding: "6px 4px",
                    textAlign: "center",
                    fontSize: 15,
                    fontWeight: 900,
                    color: "var(--tcg-navy)",
                    background: "var(--tcg-cream)",
                  }}
                />
                <div
                  style={{
                    background: d.bg,
                    color: d.fg,
                    fontSize: 10,
                    fontWeight: 900,
                    padding: "4px 7px",
                    borderRadius: 6,
                    minWidth: 38,
                    textAlign: "center",
                    letterSpacing: "0.02em",
                  }}
                >
                  {d.badge}
                </div>
                {p.origin === "manual" && canDelete && (
                  <button
                    type="button"
                    aria-label={`Elimina ${p.sku}`}
                    title="Elimina prodotto"
                    onClick={() => setToDelete(p)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      border: "1.5px solid var(--tcg-danger)",
                      background: "var(--tcg-danger-bg)",
                      color: "var(--tcg-danger)",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {(err || msg) && (
        <div style={{ padding: "12px 18px 0" }}>
          {err && (
            <Banner tone="danger" icon="x">
              {err}
            </Banner>
          )}
          {msg && !err && (
            <Banner tone="success" icon="check">
              {msg}
            </Banner>
          )}
        </div>
      )}

      <div style={{ padding: "14px 18px 0" }}>
        <Button
          variant="yellow"
          block
          disabled={busy || deltas.length === 0}
          onClick={apply}
        >
          <Icon name="check" size={16} />
          {busy
            ? "Applicazione…"
            : deltas.length === 0
              ? "Nessun delta da applicare"
              : `Applica correzioni · ${deltas.length} delta`}
        </Button>
        <div style={{ fontSize: 10, color: "var(--tcg-muted)", textAlign: "center", marginTop: 8, fontWeight: 600 }}>
          Crea evento <span className="tcg-mono" style={{ color: "var(--tcg-ink-soft)" }}>inventory_adjust</span>
        </div>
      </div>

      <div style={{ height: 24 }} />

      <ManualProductModal
        open={manualOpen}
        onClose={() => {
          setManualOpen(false);
          void refetch();
        }}
      />

      <Modal
        open={toDelete !== null}
        onClose={() => (deleteBusy ? undefined : setToDelete(null))}
        tone="danger"
        title="Elimina prodotto"
        footer={
          <>
            <Button variant="ghost" block onClick={() => setToDelete(null)} disabled={deleteBusy}>
              Annulla
            </Button>
            <Button variant="danger" block onClick={confirmDelete} disabled={deleteBusy}>
              <Icon name="trash" size={14} /> {deleteBusy ? "Eliminazione…" : "Elimina"}
            </Button>
          </>
        }
      >
        {toDelete && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              Eliminare <strong>{toDelete.name}</strong>?
            </div>
            <div className="tcg-mono" style={{ fontSize: 11, color: "var(--tcg-muted)", marginBottom: 12 }}>
              {toDelete.sku}
            </div>
            <Banner tone="warn" icon="info">
              Il prodotto verrà rimosso dal catalogo. Lo storico dei movimenti resta a fini di audit.
              L'operazione non è reversibile.
            </Banner>
          </div>
        )}
      </Modal>
    </>
  );
}

function ProductThumb({
  imageUrl,
  fallback,
  warnNoShopify,
}: {
  imageUrl: string | null;
  fallback: CategoryMeta;
  warnNoShopify?: boolean;
}) {
  const [errored, setErrored] = useState(false);
  const showImg = imageUrl && !errored;
  return (
    <div
      style={{
        position: "relative",
        width: 40,
        height: 40,
        borderRadius: 9,
        overflow: "visible",
        flexShrink: 0,
        background: showImg ? "var(--tcg-bg)" : fallback.thumbBg,
        color: fallback.thumbFg,
      }}
    >
      {showImg ? (
        <img
          src={imageUrl!}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            borderRadius: 9,
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 800,
            borderRadius: 9,
          }}
        >
          {fallback.thumbLabel}
        </div>
      )}
      {warnNoShopify && (
        <span
          title="Senza match Shopify"
          aria-label="senza match Shopify"
          style={{
            position: "absolute",
            top: -3,
            right: -3,
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "var(--tcg-warn)",
            border: "2px solid var(--tcg-card)",
          }}
        />
      )}
    </div>
  );
}

function SessionStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "success" | "warn" | "muted";
}) {
  const palette: Record<typeof tone, { bg: string; fg: string }> = {
    success: { bg: "var(--tcg-success-bg)", fg: "var(--tcg-success)" },
    warn: { bg: "var(--tcg-warn-bg)", fg: "var(--tcg-warn)" },
    muted: { bg: "var(--tcg-bg)", fg: "var(--tcg-muted)" },
  };
  const p = palette[tone];
  return (
    <div style={{ background: p.bg, padding: "8px 10px", borderRadius: 10 }}>
      <div className="tcg-num" style={{ fontSize: 18, fontWeight: 900, color: p.fg }}>
        {value}
      </div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: p.fg,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginTop: 1,
        }}
      >
        {label}
      </div>
    </div>
  );
}

type DeltaBadge = { badge: string; bg: string; fg: string };

function deltaInfo(counted: number | "" | undefined, sys: number): DeltaBadge {
  if (counted === undefined || counted === "") {
    return { badge: "—", bg: "var(--tcg-bg)", fg: "var(--tcg-muted)" };
  }
  const d = counted - sys;
  if (d === 0) {
    return { badge: "✓", bg: "var(--tcg-success-bg)", fg: "var(--tcg-success)" };
  }
  const abs = Math.abs(d);
  const sign = d > 0 ? "+" : "";
  if (abs <= 5) {
    return { badge: `${sign}${d}`, bg: "var(--tcg-warn-bg)", fg: "var(--tcg-warn)" };
  }
  return { badge: `${sign}${d}`, bg: "var(--tcg-danger-bg)", fg: "var(--tcg-danger)" };
}

type CategoryMeta = {
  thumbBg: string;
  thumbFg: string;
  thumbLabel: string;
};

function inferCategory(p: Product): CategoryMeta {
  const cat = (p.category || "").toLowerCase();
  const skuFirst = (p.sku.split("-")[0] || "").toUpperCase();
  if (cat.startsWith("poke") || skuFirst === "POK")
    return { thumbBg: "#FFE4D6", thumbFg: "#9A3412", thumbLabel: "POK" };
  if (cat.replace(/\s|_/g, "") === "onepiece" || skuFirst === "OP")
    return { thumbBg: "#DBEAFE", thumbFg: "#1E40AF", thumbLabel: "OP" };
  if (cat.startsWith("yugio") || cat.startsWith("yu-gi") || skuFirst === "YGO")
    return { thumbBg: "#FCE7F3", thumbFg: "#9D174D", thumbLabel: "YGO" };
  if (cat.startsWith("lorcana") || skuFirst === "LOR")
    return { thumbBg: "#FFEDD5", thumbFg: "#C2410C", thumbLabel: "LOR" };
  if (cat.startsWith("funko") || skuFirst === "FUN")
    return { thumbBg: "#CCFBF1", thumbFg: "#0F766E", thumbLabel: "FUN" };
  if (cat.includes("dragon") || skuFirst === "DB")
    return { thumbBg: "#FEF3C7", thumbFg: "#92400E", thumbLabel: "DB" };
  if (cat.includes("magic") || skuFirst === "MTG")
    return { thumbBg: "#E6EBF8", thumbFg: "#1B3A8C", thumbLabel: "MTG" };
  return { thumbBg: "#F4F1EA", thumbFg: "#3E4255", thumbLabel: skuFirst.slice(0, 3) || "???" };
}
